import { Component, Input } from '@angular/core';

export type SpinnerType = 'border' | 'grow';
export type SpinnerSize = 'sm' | 'md' | 'lg';
export type SpinnerVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

/**
 * Spinner Component
 *
 * A loading indicator component with various styles and colors.
 *
 * Usage:
 * ```html
 * <!-- Basic spinner -->
 * <app-spinner></app-spinner>
 *
 * <!-- Different types -->
 * <app-spinner type="border"></app-spinner>
 * <app-spinner type="grow"></app-spinner>
 *
 * <!-- Different sizes -->
 * <app-spinner size="sm"></app-spinner>
 * <app-spinner size="md"></app-spinner>
 * <app-spinner size="lg"></app-spinner>
 *
 * <!-- Different colors -->
 * <app-spinner variant="primary"></app-spinner>
 * <app-spinner variant="success"></app-spinner>
 * <app-spinner variant="danger"></app-spinner>
 *
 * <!-- With label -->
 * <app-spinner label="Loading..."></app-spinner>
 *
 * <!-- Centered in container -->
 * <app-spinner [centered]="true"></app-spinner>
 *
 * <!-- Full page overlay -->
 * <app-spinner [overlay]="true" label="Please wait..."></app-spinner>
 * ```
 */
@Component({
  selector: 'app-spinner',
  standalone: false,
  templateUrl: './spinner.component.html',
  styleUrl: './spinner.component.scss'
})
export class SpinnerComponent {
  /** Type of spinner: border or grow */
  @Input() type: SpinnerType = 'border';

  /** Size of spinner */
  @Input() size: SpinnerSize = 'md';

  /** Color variant */
  @Input() variant: SpinnerVariant = 'primary';

  /** Optional loading text label */
  @Input() label = '';

  /** Whether to center the spinner */
  @Input() centered = false;

  /** Whether to display as a full-page overlay */
  @Input() overlay = false;

  /** Custom accessibility label */
  @Input() srText = 'Loading...';

  get spinnerClass(): string {
    const classes: string[] = [];

    // Type class
    classes.push(`spinner-${this.type}`);

    // Size class (only sm is built-in, others are custom)
    if (this.size === 'sm') {
      classes.push(`spinner-${this.type}-sm`);
    } else if (this.size === 'lg') {
      classes.push('spinner-lg');
    }

    // Color class
    classes.push(`text-${this.variant}`);

    return classes.join(' ');
  }
}
